import { useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigationType, useParams } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AOS from 'aos';
import './index.css';

import Header from './components/Header';
import Hero from './components/Hero';
import Stats from './components/Stats';
import Services from './components/Services';
import Solutions from './components/Solutions';
import RealisationsPreview from './components/RealisationsPreview';
import CataloguePreview from './components/CataloguePreview';
import Process from './components/Process';
import Testimonials from './components/Testimonials';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import WhatsAppFab from './components/WhatsAppFab';
import ProtectedRoute from './components/admin/ProtectedRoute';

// SEO Pages
import SEO from './components/SEO';
import ZonesIntervention from './components/ZonesIntervention';

// SEO Data
import { cityData } from './data/cityData';
import { serviceData } from './data/serviceData';

// Dynamic / Lazy loaded pages
const RealisationsPage = lazy(() => import('./pages/RealisationsPage'));
const ProjectDetailsPage = lazy(() => import('./pages/ProjectDetailsPage'));
const CataloguePage = lazy(() => import('./pages/CataloguePage'));
const ProductDetailsPage = lazy(() => import('./pages/ProductDetailsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const CityPage = lazy(() => import('./pages/CityPage'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));

gsap.registerPlugin(ScrollTrigger);

// Kill all GSAP ScrollTriggers and scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  
  useLayoutEffect(() => {
    // Just kill triggers. Do NOT use t.kill(true) or manual DOM unwrapping 
    // because it mutates the DOM before React unmounts it, causing fatal crashes (black screen).
    ScrollTrigger.getAll().forEach(t => t.kill());

    document.body.removeAttribute('style');
    document.documentElement.removeAttribute('style');
    const main = document.querySelector('main');
    if (main) main.removeAttribute('style');
    
    if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);
  return null;
}

function HomePage() {
  return (
    <>
      <SEO 
        title="Luxury Wood Concept — Menuiserie Sur Mesure Haut de Gamme au Maroc"
        description="Luxury Wood Concept crée des espaces de vie exceptionnels à Rabat, Casablanca et Marrakech. Spécialiste en cuisines, portes, dressings et aménagements de villas sur mesure."
        path="/"
      />
      <Hero />
      <Stats />
      <Services />
      <Solutions />
      <RealisationsPreview />
      <CataloguePreview />
      <Process />
      <Testimonials />
      <ZonesIntervention />
      <CTASection />
    </>
  );
}

/**
 * DynamicSlugRouter — Routes dynamiques pour villes ET services
 */
function DynamicSlugRouter() {
  const { slug } = useParams();
  
  if (cityData[slug]) {
    return <CityPage />;
  }
  
  if (serviceData[slug]) {
    return <ServicePage />;
  }
  
  return <Navigate to="/" replace />;
}

// Simple fallback loader
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--bg-primary)' }}>
    <div style={{ width: '40px', height: '40px', border: '3px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/realisations" element={<RealisationsPage />} />
            <Route path="/realisation/:id" element={<ProjectDetailsPage />} />
            <Route path="/catalogue" element={<CataloguePage />} />
            <Route path="/produit/:id" element={<ProductDetailsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/partenaires" element={<PartnersPage />} />
            
            {/* Login & Admin Routes */}
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Dynamic routes: cities + services (must be last) */}
            <Route path="/:slug" element={<DynamicSlugRouter />} />
          </Routes>
        </Suspense>
      </main>
      
      {!isAdmin && (
        <>
          <Footer />
          <WhatsAppFab />
        </>
      )}
    </>
  );
}

function App() {
  useEffect(() => {
    // Inject aos.css dynamically
    if (!document.getElementById('aos-css')) {
      const link = document.createElement('link');
      link.id = 'aos-css';
      link.rel = 'stylesheet';
      link.href = '/aos.css';
      document.head.appendChild(link);
    }
    // Init AOS after CSS is (or will be) loaded
    const timer = setTimeout(() => {
      AOS.init({
        duration: 800,
        easing: 'ease-out-cubic',
        once: true,
        offset: 60,
      });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
